/* eslint-disable jsx-a11y/anchor-has-content */

import {
	Button,
	ErrorMessage,
	Modal,
	ModalActions,
	ModalContent,
	ModalHeading,
	TextLink,
} from '@audio-network/componentlibrary'
import { Download as DownloadIcon } from '@audio-network/componentlibrary/icons'
import { Trans, useTranslation } from 'next-i18next'
import { useRef } from 'react'
import { FormProvider } from 'react-hook-form'

import sessionStore from '@/stores/sessionStore'
import { buildContactUrl } from '@/utils/contact/buildContactUrl'
import { hasStemsAccess } from '@/utils/hasStemsAccess'

import { useDownloadContext } from '../DownloadOptionsModal.context'
import { MAX_DOWNLOAD_FILES } from './DownloadOptions.constants'
import {
	useDownloadOptionsForm,
	useFileLimitViewedAnalytics,
} from './DownloadOptions.hooks'
import styles from './DownloadOptions.module.css'
import FileFormatSection from './FileFormatSection'
import MixOptionsSection from './MixOptionsSection'
import StemsOptionsSection from './StemsOptionsSection'

const DownloadOptions = (): JSX.Element => {
	const user = sessionStore((state) => state.session?.user)
	const showStemsOptionsSection = hasStemsAccess(user)
	const { t } = useTranslation('download')

	const { isLoading, modalId } = useDownloadContext()
	const downloadOptionsModalRef = useRef(null)
	const {
		downloadOptionsFormId,
		downloadOptionsModalHeadingId,
		downloadTrackCount,
		downloadTrackCountLabel,
		formMethods,
		handleSubmitDownload,
		isFileLimitExceeded,
	} = useDownloadOptionsForm(downloadOptionsModalRef)

	useFileLimitViewedAnalytics(isFileLimitExceeded, downloadTrackCount)

	return (
		<Modal
			aria-labelledby={downloadOptionsModalHeadingId}
			id={modalId}
			ref={downloadOptionsModalRef}
		>
			<ModalHeading id={downloadOptionsModalHeadingId}>
				{downloadTrackCountLabel}
			</ModalHeading>
			<ModalContent>
				<FormProvider {...formMethods}>
					<form
						className={styles.form}
						id={downloadOptionsFormId}
						onSubmit={handleSubmitDownload}
					>
						<MixOptionsSection />
						{showStemsOptionsSection && <StemsOptionsSection />}
						<FileFormatSection />
					</form>
				</FormProvider>
			</ModalContent>
			<ModalActions
				className={
					isFileLimitExceeded ? styles.modalActionsWithFileLimit : undefined
				}
			>
				<div className={styles.downloadButtonWrapper}>
					{isFileLimitExceeded && (
						<ErrorMessage
							aria-live="assertive"
							id={`${downloadOptionsFormId}-file-limit`}
							role="alert"
						>
							<Trans
								components={{
									a: (
										<TextLink
											href={buildContactUrl(user)}
											size="inherit"
											target="_blank"
										/>
									),
								}}
							>
								{t('download-file-limit-exceeded', {
									count: downloadTrackCount,
									limit: MAX_DOWNLOAD_FILES,
								})}
							</Trans>
						</ErrorMessage>
					)}
					{!isFileLimitExceeded && (
						<Button
							disabled={isLoading}
							form={downloadOptionsFormId}
							fullWidth
							startIcon={<DownloadIcon />}
							type="submit"
						>
							{isLoading
								? t('loading-download-options')
								: downloadTrackCountLabel}
						</Button>
					)}
				</div>
			</ModalActions>
		</Modal>
	)
}

export default DownloadOptions
